import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Info, AlertTriangle, Snowflake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCreateShippingRule, useUpdateShippingRule } from '@/hooksApi/useShippingRule';
import { useGetCarriersIndexSetting } from '@/hooksApi';
import { useFetchListProvinces } from '@/hooksApi/useRegion';
import { toast } from '@/hooks/use-toast';
import { detectSoftLimitWarning } from '@/utils/detectSoftLimitWarning';
import { getTemperatureCoverage } from '@/utils/temperatureCoverage';
import type { ProvinceOption } from '@/utils/provinceLookup';
import { CarrierAvatar } from './CarrierAvatar';
import type {
  ShippingRule,
  ShippingRuleCreatePayload,
  ShippingRuleCondition,
  ConditionField,
  ConditionOperator,
  FilterByCriteria,
} from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  editing?: ShippingRule;
  provinces?: ProvinceOption[];
}

type FieldKey = ConditionField;

interface DraftCondition {
  field: FieldKey | '';
  operator: ConditionOperator | '';
  rawValue: string; // free-text, comma-separated for multi-value
}

const FIELD_OPS: Record<FieldKey, ConditionOperator[]> = {
  source: ['is', 'is_not'],
  destination: ['is', 'is_not'],
  package_size: ['is', 'is_not'],
  order_type: ['is', 'is_not'],
  qlue: ['is'],
  fragile: ['is'],
  // TEMP-9 — cold-chain flag. Boolean like fragile/qlue; only `is` makes
  // sense (a rule keyed on "temperature_control is Yes/No").
  temperature_control: ['is'],
  // SR-FB10 (May 2026 feedback) — equality is rarely what merchants want
  // at a money threshold; backend now rejects `eq` for cod_amount. We
  // surface gte/lte instead.
  cod_amount: ['gt', 'lt', 'gte', 'lte'],
};

// SR-FB6 (May 2026 feedback) — Source is always Baghdad in current ops,
// so the option is hidden from new rules. We keep it in FIELD_KEYS for
// the editing path so historic rules with `source` conditions still
// render their saved value, but the create-mode picker filters it out.
const FIELD_KEYS: FieldKey[] = [
  'source',
  'destination',
  'package_size',
  'order_type',
  'qlue',
  'fragile',
  'temperature_control',
  'cod_amount',
];

const FIELD_KEYS_FOR_NEW_RULES: FieldKey[] = FIELD_KEYS.filter((k) => k !== 'source');

// SR-FB1 — Package Size value choices. Backend accepts any of these
// short codes / synonyms (EliminateFilter::normalizePackageSize).
const PACKAGE_SIZE_OPTIONS: Array<{ value: string; key: string }> = [
  { value: 'XS', key: 'shipping_rules.sizes.xs' },
  { value: 'S', key: 'shipping_rules.sizes.s' },
  { value: 'M', key: 'shipping_rules.sizes.m' },
  { value: 'L', key: 'shipping_rules.sizes.l' },
  { value: 'XL', key: 'shipping_rules.sizes.xl' },
];

// SR-FB2 — Order Type value choices. Maps to backend enum
// (DEFAULT/RETURN/EXCHANGE).
const ORDER_TYPE_OPTIONS: Array<{ value: string; key: string }> = [
  { value: 'DEFAULT', key: 'shipping_rules.order_types.normal' },
  { value: 'RETURN', key: 'shipping_rules.order_types.return' },
  { value: 'EXCHANGE', key: 'shipping_rules.order_types.exchange' },
];

function parseValue(field: FieldKey, raw: string): ShippingRuleCondition['value'] {
  if (field === 'cod_amount') {
    const n = Number(raw.replace(/[, ]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  if (field === 'qlue' || field === 'fragile' || field === 'temperature_control') {
    const v = raw.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1';
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildPayload(
  name: string,
  priority: number,
  filterBy: FilterByCriteria,
  drafts: DraftCondition[],
  carrierUids: string[],
  isOverridable: boolean,
): ShippingRuleCreatePayload | null {
  if (!name.trim() || carrierUids.length === 0) return null;

  const conditions: ShippingRuleCondition[] = [];
  for (const d of drafts) {
    if (!d.field || !d.operator) continue;
    if (!d.rawValue.trim()) continue;
    conditions.push({
      field: d.field,
      operator: d.operator,
      value: parseValue(d.field, d.rawValue),
    });
  }
  if (conditions.length === 0) return null;

  return {
    name: name.trim(),
    priority,
    is_active: true,
    is_overridable: isOverridable,
    filter_by_criteria: filterBy,
    conditions,
    carrier_uids: carrierUids,
  };
}

function valueToRaw(field: FieldKey, value: ShippingRuleCondition['value']): string {
  if (field === 'qlue' || field === 'fragile' || field === 'temperature_control') {
    const v = Array.isArray(value) ? (value as boolean[])[0] : (value as boolean);
    return v ? 'Yes' : 'No';
  }
  if (field === 'cod_amount') {
    return String(Array.isArray(value) ? ((value as number[])[0] ?? '') : (value as number));
  }
  if (field === 'package_size' || field === 'order_type') {
    // Item 8 (May 2026) — package_size / order_type are now MULTI-select.
    // The backend stores `value` as a JSON array of strings; we keep every
    // element so a rule like {size: [S, M]} round-trips through the builder
    // as a single multi-value row (not just value[0]).
    if (Array.isArray(value)) return value.map(String).filter(Boolean).join(', ');
    return String(value ?? '');
  }
  if (Array.isArray(value)) return value.map(String).join(', ');
  return String(value);
}

// Item 8 — multi-value `IS`/`IS_NOT` fields. For these, two condition rows
// with the SAME field + SAME operator are semantically one OR-set; the
// builder collapses them into a single multi-select row. cod_amount is
// excluded (its operators are numeric thresholds — a min+max range is two
// legitimate rows). qlue/fragile are excluded (boolean, single value).
const MULTI_VALUE_FIELDS: ReadonlySet<FieldKey> = new Set<FieldKey>([
  'source',
  'destination',
  'package_size',
  'order_type',
]);

/**
 * Collapse legacy "same field + same operator" condition rows into one
 * multi-select row. Stored data may still be split (the backend has a
 * data-repair migration, but anything not yet collapsed must render as one
 * clean row). Order is preserved by first occurrence; values are de-duped.
 */
function mergeLegacyConditions(conditions: ShippingRuleCondition[]): DraftCondition[] {
  const drafts: DraftCondition[] = [];
  const indexByKey = new Map<string, number>();
  for (const c of conditions) {
    const raw = valueToRaw(c.field, c.value);
    const mergeable = MULTI_VALUE_FIELDS.has(c.field);
    const key = `${c.field}|${c.operator}`;
    if (mergeable && indexByKey.has(key)) {
      const idx = indexByKey.get(key)!;
      const existing = drafts[idx].rawValue
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const incoming = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const merged = [...existing];
      for (const v of incoming) if (!merged.includes(v)) merged.push(v);
      drafts[idx] = { ...drafts[idx], rawValue: merged.join(', ') };
      continue;
    }
    drafts.push({ field: c.field, operator: c.operator, rawValue: raw });
    if (mergeable) indexByKey.set(key, drafts.length - 1);
  }
  return drafts;
}

export function ShippingRuleBuilderModal({ open, onClose, editing, provinces = [] }: Props) {
  const { t, i18n } = useTranslation();
  const dir = (i18n.dir?.() ?? 'ltr') as 'rtl' | 'ltr';
  const locale = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const { data: carriersData } = useGetCarriersIndexSetting();
  // Task 5 — coverage badge on the carrier picker needs province names to
  // resolve carrier.attributes.temperature_provinces codes into display
  // names (same cached query key as the rest of the app, no extra cost).
  const { data: provincesData } = useFetchListProvinces();
  const provinceRows = provincesData?.object ?? [];
  const { mutate: createMutate, isLoading: isCreating } = useCreateShippingRule();
  const { mutate: updateMutate, isLoading: isUpdating } = useUpdateShippingRule(editing?.uid ?? '');
  const isLoading = isCreating || isUpdating;

  const [name, setName] = useState('');
  // Item 7 — priority is held as a string so a cleared field stays empty
  // (instead of silently becoming 0). The numeric value is derived only at
  // validation/submit time. Create-mode default is '10'.
  const [priority, setPriority] = useState('10');
  const [filterBy, setFilterBy] = useState<FilterByCriteria>(null);
  // SRE-3 — when on, the rule is advisory (merchant may pick off-rule at order
  // time). Default off = strict/binding, matching the backend column default.
  const [isOverridable, setIsOverridable] = useState(false);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<DraftCondition[]>([
    { field: '', operator: '', rawValue: '' },
  ]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // QA bug May 2026 — when the backend returns 400 with
  // object.carrier_uids[], we flag the offending carrier chips inline so
  // the merchant doesn't have to read a long toast to know which one to
  // drop. Keyed by carrier_uid. Cleared on any carrier/condition change.
  const [carrierOffenders, setCarrierOffenders] = useState<
    Record<string, { field: string; reason: string; message: string }>
  >({});
  // SR-FB11 (May 2026) — when backend rejects a duplicate priority we
  // surface the localized message + the clashing rule name inline below
  // the Priority input. Cleared when the merchant edits the value.
  const [priorityError, setPriorityError] = useState<string | null>(null);

  // Sync form state on each open. Keying on `open` + `editing?.uid` so the
  // modal resets between edit→cancel→create cycles. Without resetting, the
  // form would keep the previous edit's data when the user clicks +New rule
  // afterwards (RS-32 follow-up — modal state leaked between modes).
  //
  // SR-FB5 (May 2026 feedback) — editing the SAME rule twice was leaving the
  // form half-stale because the deps were `[open, editing?.uid]`. When a
  // merchant edited, closed, then re-opened the same row, neither value
  // changed and the effect didn't fire — so any uncommitted local edits
  // from the previous session persisted. Adding `editing?.updated_at` plus
  // the array references makes the sync re-run on every open(editing→x→x).
  useEffect(() => {
    if (!open) return;
    // BUG-SR-001 — backend-error state (carrierOffenders + priorityError) must
    // be cleared on EVERY open so the merchant never sees a red banner or
    // chip warning from a previous session. Cleared up front so both edit and
    // create branches start clean.
    setCarrierOffenders({});
    setPriorityError(null);
    if (editing) {
      setName(editing.name);
      setPriority(String(editing.priority));
      setFilterBy(editing.filter_by_criteria);
      setIsOverridable(Boolean(editing.is_overridable));
      setCarriers((editing.carriers ?? []).map((c) => c.uid));
      // Item 8 — collapse any legacy split rows (e.g. two `package_size IS`
      // rows) into a single multi-select row so the merchant sees one clean
      // row and saving normalizes the stored shape.
      const incoming = mergeLegacyConditions(editing.conditions ?? []);
      setDrafts(incoming.length ? incoming : [{ field: '', operator: '', rawValue: '' }]);
    } else {
      // Create mode — clear any leftover state from a prior edit.
      setName('');
      setPriority('10');
      setFilterBy(null);
      setIsOverridable(false);
      setCarriers([]);
      setDrafts([{ field: '', operator: '', rawValue: '' }]);
      setSubmitAttempted(false);
    }
  }, [open, editing?.uid, editing?.updated_at, editing?.conditions, editing?.carriers]);

  const availableCarriers = useMemo(
    () => (carriersData?.object ?? []).filter((c: any) => c.is_active),
    [carriersData],
  );

  // Item 7 — numeric view of the priority string. Empty / non-numeric input
  // yields NaN so the validator below can reject it. The minimum priority is
  // 1; 0, negatives, and a blank field are never allowed to be saved.
  const priorityValue = useMemo(() => {
    const trimmed = priority.trim();
    if (trimmed === '') return NaN;
    return Number(trimmed);
  }, [priority]);

  // Inline validation state, surfaced both on Save click and as live
  // error chips so the merchant always knows why Save isn't working.
  const validationErrors = useMemo(() => {
    const errs: { field: string; message: string }[] = [];
    if (!name.trim())
      errs.push({ field: 'name', message: t('shipping_rules.errors_extra.name_required') });
    // Item 7 — priority must be a whole number >= 1. Blank/0/negative/decimal
    // are all rejected before the payload is ever built.
    if (!Number.isInteger(priorityValue) || priorityValue < 1)
      errs.push({ field: 'priority', message: t('shipping_rules.errors_extra.priority_min') });
    if (carriers.length === 0)
      errs.push({ field: 'carriers', message: t('shipping_rules.no_carriers_picked') });
    const validConds = drafts.filter((d) => d.field && d.operator && d.rawValue.trim());
    if (validConds.length === 0)
      errs.push({
        field: 'conditions',
        message: t('shipping_rules.errors_extra.at_least_one_condition'),
      });
    return errs;
  }, [name, priorityValue, carriers, drafts, t]);

  // Item 8 — duplicate (field, operator) guard. With multi-select value
  // inputs the merchant never needs a second row of the same field+operator
  // pair (that was exactly the unsatisfiable `size==S AND size==M` shape).
  // We record every (field, operator) pair already chosen so each row can
  // disable the pairs owned by *other* rows: a field becomes unpickable once
  // all its operators are taken, and an operator becomes unpickable once that
  // exact pair exists elsewhere.
  const usedPairsByRow = useMemo(() => {
    return drafts.map((_d, selfIdx) => {
      const pairs = new Set<string>();
      const fields = new Set<FieldKey>();
      drafts.forEach((other, otherIdx) => {
        if (otherIdx === selfIdx) return;
        if (!other.field || !other.operator) return;
        pairs.add(`${other.field}|${other.operator}`);
      });
      // A field is fully consumed when every operator it supports is already
      // paired up in some other row.
      (Object.keys(FIELD_OPS) as FieldKey[]).forEach((f) => {
        if (FIELD_OPS[f].every((op) => pairs.has(`${f}|${op}`))) fields.add(f);
      });
      return { pairs, exhaustedFields: fields };
    });
  }, [drafts]);

  if (!open) return null;

  const reset = () => {
    setName('');
    setPriority('10');
    setFilterBy(null);
    setIsOverridable(false);
    setCarriers([]);
    setDrafts([{ field: '', operator: '', rawValue: '' }]);
    setCarrierOffenders({});
    setPriorityError(null);
  };

  const submit = () => {
    setSubmitAttempted(true);
    if (validationErrors.length > 0) {
      // Surface a top-level toast so the merchant gets immediate feedback
      // even if the inline errors are below the fold.
      toast({
        title: t('shipping_rules.errors_extra.fix_before_save'),
        duration: 4000,
      });
      return;
    }
    const payload = buildPayload(name, priorityValue, filterBy, drafts, carriers, isOverridable);
    if (!payload) return;
    const onSuccess = (data: any) => {
      const warn = detectSoftLimitWarning(data?.headers ?? {}) ?? data?.soft_limit_warning;
      if (warn) {
        toast({
          title: t('shipping_rules.soft_limit_warning'),
          duration: 6000,
        });
      }
      toast({
        title: editing
          ? t('shipping_rules.errors_extra.saved_edit')
          : t('shipping_rules.errors_extra.saved_create'),
        duration: 3000,
      });
      reset();
      setSubmitAttempted(false);
      onClose();
    };
    const onError = (err: any) => {
      // QA bug May 2026 — when the backend rejects with the structured
      // carrier-incompatibility shape (object.carrier_uids[]), surface
      // per-chip warnings inline so the merchant can act immediately.
      const offenders = err?.response?.data?.object?.carrier_uids;
      if (Array.isArray(offenders) && offenders.length > 0) {
        const byUid: typeof carrierOffenders = {};
        offenders.forEach((o: any) => {
          if (!o?.carrier_uid) return;
          byUid[o.carrier_uid] = {
            field: String(o.field ?? ''),
            reason: String(o.reason ?? ''),
            message: String(o.message ?? ''),
          };
        });
        setCarrierOffenders(byUid);
      }
      // SR-FB11 — surface duplicate-priority error inline.
      const data = err?.response?.data;
      if (data?.object?.priority?.clashes_with) {
        setPriorityError(String(data.message ?? ''));
      }
      // Always surface a toast — message is already localized by the
      // backend (lang param via the axios interceptor).
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('shipping_rules.errors_extra.save_failed');
      toast({ title: String(msg), duration: 5000 });
    };
    if (editing) {
      updateMutate(payload, { onSuccess, onError } as any);
    } else {
      createMutate(payload, { onSuccess, onError } as any);
    }
  };

  const showError = (field: string): string | undefined =>
    submitAttempted ? validationErrors.find((e) => e.field === field)?.message : undefined;

  const updateDraft = (idx: number, patch: Partial<DraftCondition>) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
    // Conditions changed → previous backend offender markers may be stale.
    if (Object.keys(carrierOffenders).length > 0) setCarrierOffenders({});
  };

  const addDraft = () => {
    setDrafts((prev) => [...prev, { field: '', operator: '', rawValue: '' }]);
  };

  const removeDraft = (idx: number) => {
    setDrafts((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const toggleCarrier = (uid: string) => {
    setCarriers((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));
    // Toggling a carrier changes the set the backend would evaluate
    // against — drop the stale offender markers so the merchant doesn't
    // see a red ring on a chip they just removed/added.
    setCarrierOffenders((prev) => {
      const next: typeof prev = {};
      Object.keys(prev).forEach((u) => {
        if (u !== uid && carriers.includes(u)) next[u] = prev[u];
      });
      return next;
    });
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
      dir={dir}
    >
      <div className='w-full max-w-2xl rounded-2xl bg-sd-card shadow-2xl max-h-[90vh] flex flex-col overflow-hidden'>
        {/* Sticky Header */}
        <div className='shrink-0 flex items-start justify-between gap-4 px-6 py-4 border-b border-sd-border bg-sd-card'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold leading-tight'>
              {editing ? t('shipping_rules.edit') : t('shipping_rules.create_button')}
            </h2>
            <p className='text-xs text-sd-muted-foreground mt-0.5'>
              {t('shipping_rules.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('cancel')}
            className='shrink-0 rounded-lg p-1.5 text-sd-muted-foreground hover:bg-sd-muted hover:text-sd-foreground transition-colors'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className='flex-1 overflow-y-auto px-6 py-5 space-y-5'>
          {/* Name */}
          <div className='space-y-1'>
            <label className='text-sm font-medium'>{t('name')}</label>
            <input
              data-testid='shipping-rule-name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className={
                'w-full h-10 rounded-md border bg-sd-input-bg px-3 text-sm text-start ' +
                (showError('name') ? 'border-red-400' : 'border-sd-border')
              }
              placeholder={t('shipping_rules.name_placeholder')}
            />
            {showError('name') && <p className='text-xs text-red-600 mt-1'>{showError('name')}</p>}
          </div>

          {/* Priority + Filter */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <label className='text-sm font-medium'>{t('shipping_rules.priority_label')}</label>
              <input
                data-testid='shipping-rule-priority'
                type='number'
                // Item 7 — priority must never be 0 or below. `min`/`step`
                // give the native control its spinner floor; validation
                // (validationErrors) is the real guard since `min` alone does
                // not block a typed-in or pasted 0.
                min={1}
                step={1}
                value={priority}
                onChange={(e) => {
                  // Store the raw string so a cleared field stays empty
                  // instead of collapsing to 0 — an empty field is rejected
                  // by validation, never silently saved as 0.
                  setPriority(e.target.value);
                  // SR-FB11 — drop stale duplicate-priority error.
                  if (priorityError) setPriorityError(null);
                }}
                className={
                  'w-full h-10 rounded-md border bg-sd-input-bg px-3 text-sm text-start ' +
                  (priorityError || showError('priority') ? 'border-red-400' : 'border-sd-border')
                }
                aria-invalid={priorityError || showError('priority') ? true : undefined}
              />
              {priorityError || showError('priority') ? (
                <p className='text-xs text-red-600 mt-1' data-testid='priority-error'>
                  {priorityError ?? showError('priority')}
                </p>
              ) : (
                <p className='text-xs text-sd-muted-foreground'>
                  {t('shipping_rules.priority_hint')}
                </p>
              )}
            </div>
            <div className='space-y-1'>
              <label className='text-sm font-medium'>
                {t('shipping_rules.filter_by_criteria.label')}
              </label>
              <select
                data-testid='shipping-rule-filter'
                value={filterBy ?? ''}
                onChange={(e) => setFilterBy((e.target.value || null) as FilterByCriteria)}
                className='w-full h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start'
              >
                <option value=''>{t('shipping_rules.filter_by_criteria.none')}</option>
                <option value='cheapest'>{t('shipping_rules.filter_by_criteria.cheapest')}</option>
                {/* most_reliable / fastest gated on Phase C metrics aggregator
                    (carriers.reliability_score / avg_delivery_hours) — until
                    that ships, picking either silently throws CriteriaNotReady
                    on rule resolution. Keep the option only when editing an
                    existing rule that already uses it, so the merchant can
                    still see/change it. */}
                {editing?.filter_by_criteria === 'most_reliable' && (
                  <option value='most_reliable'>
                    {t('shipping_rules.filter_by_criteria.most_reliable')}
                  </option>
                )}
                {editing?.filter_by_criteria === 'fastest' && (
                  <option value='fastest'>{t('shipping_rules.filter_by_criteria.fastest')}</option>
                )}
              </select>
            </div>
          </div>

          {/* SRE-3 — overridable toggle. When ON the rule is advisory: at
              order time the merchant sees the full carrier list and may pick
              a carrier outside this rule. When OFF (default) the rule binds.
              Uses the design-system Switch (same construction as the rule/carrier
              cards) so the control reads as an interactive toggle, not an inert
              native checkbox. */}
          <div className='flex items-center justify-between gap-4 rounded-xl border border-sd-border bg-sd-muted/20 p-4'>
            <label
              htmlFor='shipping-rule-overridable'
              className='flex flex-col cursor-pointer select-none'
            >
              <span className='text-sm font-medium'>{t('shipping_rules.overridable_label')}</span>
              <span className='text-xs text-sd-muted-foreground'>
                {t('shipping_rules.overridable_hint')}
              </span>
            </label>
            <Switch
              id='shipping-rule-overridable'
              data-testid='shipping-rule-overridable'
              checked={isOverridable}
              onCheckedChange={setIsOverridable}
              className='data-[state=checked]:bg-sd-primary shrink-0'
            />
          </div>

          {/* WHEN section: conditions */}
          <div className='space-y-3 rounded-xl border border-sd-border bg-sd-muted/20 p-4'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <div className='flex items-center gap-2'>
                  <span className='inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold'>
                    1
                  </span>
                  <h3 className='text-base font-semibold'>{t('shipping_rules.section_when')}</h3>
                </div>
                <p className='text-xs text-sd-muted-foreground mt-1 ms-8 inline-flex items-center gap-1'>
                  <Info className='h-3.5 w-3.5 shrink-0' />
                  {t('shipping_rules.section_when_hint')}
                </p>
              </div>
              <Button
                data-testid='add-condition-btn'
                variant='outline'
                size='sm'
                onClick={addDraft}
                disabled={drafts.length >= FIELD_KEYS.length}
                className='h-8 text-xs gap-1'
              >
                <Plus className='h-3.5 w-3.5' />
                {t('shipping_rules.add_condition_btn')}
              </Button>
            </div>
            {showError('conditions') && (
              <p className='text-xs text-red-600'>{showError('conditions')}</p>
            )}
            {drafts.map((d, idx) => {
              const allowedOps = d.field ? FIELD_OPS[d.field] : [];
              const isProvinceField = d.field === 'source' || d.field === 'destination';
              const isBoolField =
                d.field === 'qlue' || d.field === 'fragile' || d.field === 'temperature_control';
              const isNumericField = d.field === 'cod_amount';
              const isPackageSizeField = d.field === 'package_size';
              const isOrderTypeField = d.field === 'order_type';
              // SR-FB6 — when creating a new rule, drop `source` from the
              // pickable fields. When editing an existing rule that
              // already has a `source` condition, the value stays editable
              // (the saved value can be modified or removed) but the
              // field option list still hides Source to prevent adding a
              // *new* row with source.
              const fieldOptions =
                editing && d.field === 'source' ? FIELD_KEYS : FIELD_KEYS_FOR_NEW_RULES;
              // Item 8 — pairs/fields already consumed by OTHER rows.
              const { pairs: usedPairs, exhaustedFields } = usedPairsByRow[idx];
              return (
                <div
                  key={idx}
                  dir={dir}
                  className='grid grid-cols-12 gap-3 rounded-lg border border-sd-border bg-sd-card p-3 items-start'
                >
                  {/* Field */}
                  <div className='col-span-4 flex flex-col gap-1.5'>
                    <label className='text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                      {t('shipping_rules.field_label')}
                    </label>
                    <select
                      data-testid={`condition-field-${idx}`}
                      value={d.field}
                      onChange={(e) =>
                        updateDraft(idx, {
                          field: e.target.value as FieldKey,
                          operator: '',
                          rawValue: '',
                        })
                      }
                      className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start'
                    >
                      <option value=''>{t('shipping_rules.value_placeholder_select')}</option>
                      {fieldOptions.map((f) => (
                        <option
                          key={f}
                          value={f}
                          // Disable a field once every (field, operator) pair
                          // it can produce is already claimed by another row.
                          // The row's own current field stays selectable.
                          disabled={f !== d.field && exhaustedFields.has(f)}
                        >
                          {t(`shipping_rules.fields.${f}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Operator */}
                  <div className='col-span-3 flex flex-col gap-1.5'>
                    <label className='text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                      {t('shipping_rules.operator_label')}
                    </label>
                    <select
                      data-testid={`condition-operator-${idx}`}
                      value={d.operator}
                      onChange={(e) =>
                        updateDraft(idx, { operator: e.target.value as ConditionOperator })
                      }
                      disabled={!d.field}
                      title={!d.field ? t('shipping_rules.select_field_first') : ''}
                      className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <option value=''>
                        {!d.field
                          ? t('shipping_rules.select_field_first')
                          : t('shipping_rules.value_placeholder_select')}
                      </option>
                      {allowedOps.map((op) => (
                        <option
                          key={op}
                          value={op}
                          // Disable an operator once this exact (field,
                          // operator) pair is already used by another row.
                          // The row's own current operator stays selectable.
                          disabled={op !== d.operator && usedPairs.has(`${d.field}|${op}`)}
                        >
                          {t(`shipping_rules.operators.${op}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Value */}
                  <div className='col-span-4 flex flex-col gap-1.5'>
                    <label className='text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
                      {t('shipping_rules.value_label')}
                    </label>
                    {isProvinceField && provinces.length > 0 ? (
                      <ProvinceMultiSelect
                        idx={idx}
                        rawValue={d.rawValue}
                        provinces={provinces}
                        disabled={!d.operator}
                        onChange={(v) => updateDraft(idx, { rawValue: v })}
                      />
                    ) : isBoolField ? (
                      <select
                        data-testid={`condition-value-${idx}`}
                        value={d.rawValue}
                        disabled={!d.operator}
                        onChange={(e) => updateDraft(idx, { rawValue: e.target.value })}
                        className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        <option value=''>
                          {!d.operator
                            ? t('shipping_rules.select_op_first')
                            : t('shipping_rules.value_placeholder_select')}
                        </option>
                        <option value='Yes'>{t('shipping_rules.bool.true')}</option>
                        <option value='No'>{t('shipping_rules.bool.false')}</option>
                      </select>
                    ) : isPackageSizeField ? (
                      // Item 8 (May 2026) — Package Size is multi-select:
                      // one row, value array (e.g. [S, M] → "size is S OR M").
                      <OptionMultiSelect
                        idx={idx}
                        rawValue={d.rawValue}
                        options={PACKAGE_SIZE_OPTIONS}
                        disabled={!d.operator}
                        onChange={(v) => updateDraft(idx, { rawValue: v })}
                      />
                    ) : isOrderTypeField ? (
                      // Item 8 (May 2026) — Order Type is multi-select:
                      // one row, value array (e.g. [RETURN, EXCHANGE]).
                      <OptionMultiSelect
                        idx={idx}
                        rawValue={d.rawValue}
                        options={ORDER_TYPE_OPTIONS}
                        disabled={!d.operator}
                        onChange={(v) => updateDraft(idx, { rawValue: v })}
                      />
                    ) : (
                      <input
                        data-testid={`condition-value-${idx}`}
                        type={isNumericField ? 'number' : 'text'}
                        value={d.rawValue}
                        onChange={(e) => updateDraft(idx, { rawValue: e.target.value })}
                        disabled={!d.operator}
                        placeholder={
                          !d.field
                            ? t('shipping_rules.select_field_first')
                            : !d.operator
                              ? t('shipping_rules.select_op_first')
                              : isNumericField
                                ? t('shipping_rules.cod_amount_placeholder')
                                : ''
                        }
                        className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start disabled:opacity-50 disabled:cursor-not-allowed'
                      />
                    )}
                  </div>
                  {/* Delete */}
                  <div className='col-span-1 flex flex-col items-center justify-end h-full pt-7'>
                    <button
                      onClick={() => removeDraft(idx)}
                      disabled={drafts.length === 1}
                      title={String(t('shipping_rules.remove_condition'))}
                      className='rounded-md p-2 hover:bg-sd-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed text-sd-muted-foreground hover:text-sd-destructive'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* THEN section: carriers */}
          <div className='space-y-3 rounded-xl border border-sd-border bg-sd-muted/20 p-4'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <div className='flex items-center gap-2'>
                  <span className='inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-100 text-green-700 text-xs font-bold'>
                    2
                  </span>
                  <h3 className='text-base font-semibold'>{t('shipping_rules.section_then')}</h3>
                </div>
                <p className='text-xs text-sd-muted-foreground mt-1 ms-8 inline-flex items-center gap-1'>
                  <Info className='h-3.5 w-3.5 shrink-0' />
                  {t('shipping_rules.section_then_hint')}
                </p>
              </div>
              <span className='text-xs text-sd-muted-foreground bg-sd-card border border-sd-border rounded-full px-2.5 py-0.5'>
                {carriers.length} / {availableCarriers.length}
              </span>
            </div>
            {availableCarriers.length === 0 ? (
              <div className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800'>
                {t('shipping_rules.errors.no_carrier_enabled')}
              </div>
            ) : (
              <div className='grid grid-cols-2 sm:grid-cols-3 gap-2'>
                {availableCarriers.map((c: any) => {
                  const selected = carriers.includes(c.uid);
                  const disabledForMerchant = !c.is_active_for_merchant;
                  const offender = selected ? carrierOffenders[c.uid] : undefined;
                  return (
                    <button
                      key={c.uid}
                      data-testid={`carrier-toggle-${c.uid}`}
                      onClick={() => toggleCarrier(c.uid)}
                      type='button'
                      title={offender?.message}
                      data-invalid={offender ? true : undefined}
                      className={
                        'group relative flex items-center gap-2.5 rounded-lg border px-3 py-2 text-start transition-all ' +
                        (offender
                          ? 'border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-700 shadow-sm'
                          : selected
                            ? 'border-sd-primary bg-sd-primary/5 shadow-sm'
                            : 'border-sd-border bg-sd-card hover:border-sd-primary/50 hover:bg-sd-muted/40')
                      }
                    >
                      {offender && (
                        <AlertTriangle
                          className='absolute -top-1.5 -end-1.5 h-4 w-4 text-red-600 bg-sd-card rounded-full p-0.5 shadow ring-1 ring-red-300'
                          aria-hidden='true'
                        />
                      )}
                      <CarrierAvatar
                        src={c.avatar?.abs_url}
                        name={c.title}
                        slug={c.slug}
                        size={32}
                        className='shrink-0'
                      />
                      <div className='flex flex-col min-w-0 flex-1'>
                        <span className='text-sm font-medium truncate capitalize'>
                          {c.title || c.slug}
                        </span>
                        {disabledForMerchant && (
                          <span className='text-[10px] text-amber-700'>
                            {t('shipping_rules.carrier_disabled_chip')}
                          </span>
                        )}
                        {/* BUG-SR-002 — capability badges so the merchant
                            knows BEFORE picking whether the carrier supports
                            Fragile / Qlue. Source: attributes payload, same
                            as carrier settings page. */}
                        <div
                          className='flex flex-wrap gap-1 mt-0.5'
                          data-testid={`carrier-caps-${c.uid}`}
                        >
                          {c.attributes?.fragility_handling === 'true' && (
                            <span
                              className='inline-flex items-center rounded-full bg-yellow-300 text-black dark:bg-yellow-400 dark:text-black text-[10px] leading-none px-1.5 py-0.5 ring-1 ring-yellow-500'
                              data-testid={`carrier-cap-fragile-${c.uid}`}
                            >
                              {String(t('shipping_rules.cap_fragile'))}
                            </span>
                          )}
                          {c.attributes?.clue_link_verification === 'true' && (
                            <span
                              className='inline-flex items-center rounded-full bg-yellow-300 text-black dark:bg-yellow-400 dark:text-black text-[10px] leading-none px-1.5 py-0.5 ring-1 ring-yellow-500'
                              data-testid={`carrier-cap-qlue-${c.uid}`}
                            >
                              {String(t('shipping_rules.cap_qlue'))}
                            </span>
                          )}
                          {/* Task 12 — merchant feedback: the prominent
                              always-visible province block (Task 11) made the
                              picker too tight when a carrier supported many
                              provinces. Now OK with hover: a compact cold-blue
                              badge sits alongside the fragile/qlue pills, and
                              the full province list (or "all provinces")
                              reveals on hover via a tooltip. */}
                          {(() => {
                            const cov = getTemperatureCoverage(c.attributes, provinceRows, locale);
                            if (!cov.show) return null;
                            const list =
                              cov.variant === 'all'
                                ? String(t('temp_all_provinces'))
                                : cov.provinceNames.join('، ');
                            return (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      data-testid={`carrier-cap-temp-${c.uid}`}
                                      className='inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white ring-1 ring-blue-700'
                                    >
                                      <Snowflake className='h-3 w-3 shrink-0' strokeWidth={2.25} />
                                      <span className='truncate'>{String(t('temperature_control_short'))}</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side='top' className='max-w-[220px] text-xs leading-relaxed'>
                                    <div className='mb-0.5 font-semibold'>
                                      {String(t('temp_supported_provinces_label'))}
                                    </div>
                                    {list}
                                    <TooltipArrow className='fill-sd-popover' />
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </div>
                      </div>
                      {/* Selection check */}
                      <span
                        className={
                          'inline-flex h-5 w-5 items-center justify-center rounded-full border shrink-0 ' +
                          (selected
                            ? 'border-sd-primary bg-sd-primary text-white'
                            : 'border-sd-border bg-sd-card')
                        }
                      >
                        {selected && (
                          <svg viewBox='0 0 16 16' fill='none' className='h-3 w-3'>
                            <path
                              d='M3.5 8L7 11.5L13 5'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {(showError('carriers') || (carriers.length === 0 && availableCarriers.length > 0)) && (
              <p
                className={'text-xs ' + (showError('carriers') ? 'text-red-600' : 'text-amber-700')}
              >
                {showError('carriers') ?? t('shipping_rules.no_carriers_picked')}
              </p>
            )}
            {Object.keys(carrierOffenders).length > 0 && (
              <ul className='mt-1 space-y-1 text-xs text-red-600 dark:text-red-300'>
                {Object.entries(carrierOffenders).map(([uid, o]) => (
                  <li key={uid} className='flex items-start gap-1.5'>
                    <AlertTriangle className='h-3 w-3 shrink-0 mt-0.5' aria-hidden='true' />
                    <span>{o.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className='shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-sd-border bg-sd-muted/30'>
          <div className='text-xs text-sd-muted-foreground min-w-0 truncate'>
            {submitAttempted && validationErrors.length > 0
              ? t('shipping_rules.errors_extra.fix_before_save')
              : !editing
                ? t('shipping_rules.section_when_hint')
                : ''}
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            <Button variant='outline' onClick={onClose} className='h-9 px-5 text-xs'>
              {t('cancel')}
            </Button>
            <Button
              data-testid='shipping-rule-submit'
              onClick={submit}
              disabled={isLoading}
              className='h-9 px-5 text-xs font-semibold bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90'
            >
              {isLoading ? t('shipping_rules.errors_extra.saving') : t('shipping_rules.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-select for province conditions. Stores province_code in the draft's
 * comma-separated rawValue (so it round-trips through buildPayload), but
 * displays the localized name for selection. The submit value is the
 * province_code, never the display name — exactly what backend rule
 * evaluation needs to match real order data.
 */
interface ProvinceMultiSelectProps {
  idx: number;
  rawValue: string;
  provinces: ProvinceOption[];
  disabled: boolean;
  onChange: (raw: string) => void;
}
function ProvinceMultiSelect({
  idx,
  rawValue,
  provinces,
  disabled,
  onChange,
}: ProvinceMultiSelectProps) {
  const { t } = useTranslation();
  const selected = rawValue
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const toggle = (code: string) => {
    const next = selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code];
    onChange(next.join(', '));
  };

  return (
    <div className='flex flex-col gap-1.5'>
      <select
        data-testid={`condition-value-${idx}`}
        value=''
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value) toggle(e.target.value);
          e.target.value = '';
        }}
        className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start disabled:opacity-50 disabled:cursor-not-allowed'
        aria-label={t('shipping_rules.any_of_hint')}
      >
        <option value=''>
          {disabled
            ? t('shipping_rules.select_op_first')
            : selected.length === 0
              ? t('shipping_rules.value_placeholder_select')
              : `${selected.length} ${t('selected')}`}
        </option>
        {provinces
          .filter((p) => !selected.includes(p.value))
          .map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
      </select>
      {selected.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {selected.map((code) => {
            const opt = provinces.find((p) => p.value === code);
            const label = opt ? opt.label : code;
            return (
              <span
                key={code}
                className='inline-flex items-center gap-1 rounded-full bg-sd-muted border border-sd-border px-2 py-0.5 text-[10px] font-medium'
              >
                {label}
                <button
                  type='button'
                  onClick={() => toggle(code)}
                  className='hover:bg-sd-border rounded-full p-0.5'
                  aria-label={`remove ${label}`}
                >
                  <X className='h-2.5 w-2.5' />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {!disabled && provinces.length > 0 && (
        <p className='text-[10px] text-sd-muted-foreground'>{t('shipping_rules.any_of_hint')}</p>
      )}
    </div>
  );
}

/**
 * Item 8 — generic multi-select for fixed-enum condition values
 * (package_size, order_type). Mirrors ProvinceMultiSelect: the picked values
 * are stored in the draft's comma-separated `rawValue`, so buildPayload emits
 * ONE condition row with `value` as a JSON array of strings (e.g. ["S","M"]).
 * The merchant never needs two rows of the same field+operator pair.
 */
interface OptionItem {
  value: string;
  key: string;
}
interface OptionMultiSelectProps {
  idx: number;
  rawValue: string;
  options: OptionItem[];
  disabled: boolean;
  onChange: (raw: string) => void;
}
function OptionMultiSelect({ idx, rawValue, options, disabled, onChange }: OptionMultiSelectProps) {
  const { t } = useTranslation();
  const selected = rawValue
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next.join(', '));
  };

  return (
    <div className='flex flex-col gap-1.5'>
      <select
        data-testid={`condition-value-${idx}`}
        value=''
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value) toggle(e.target.value);
          e.target.value = '';
        }}
        className='h-10 rounded-md border border-sd-border bg-sd-input-bg px-3 text-sm text-start disabled:opacity-50 disabled:cursor-not-allowed'
        aria-label={String(t('shipping_rules.any_of_hint'))}
      >
        <option value=''>
          {disabled
            ? t('shipping_rules.select_op_first')
            : selected.length === 0
              ? t('shipping_rules.value_placeholder_select')
              : `${selected.length} ${t('selected')}`}
        </option>
        {options
          .filter((o) => !selected.includes(o.value))
          .map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.key)}
            </option>
          ))}
      </select>
      {selected.length > 0 && (
        <div className='flex flex-wrap gap-1' data-testid={`condition-value-chips-${idx}`}>
          {selected.map((value) => {
            const opt = options.find((o) => o.value === value);
            const label = opt ? String(t(opt.key)) : value;
            return (
              <span
                key={value}
                className='inline-flex items-center gap-1 rounded-full bg-sd-muted border border-sd-border px-2 py-0.5 text-[10px] font-medium'
              >
                {label}
                <button
                  type='button'
                  onClick={() => toggle(value)}
                  className='hover:bg-sd-border rounded-full p-0.5'
                  aria-label={`${String(t('cancel'))} ${label}`}
                >
                  <X className='h-2.5 w-2.5' />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {!disabled && (
        <p className='text-[10px] text-sd-muted-foreground'>{t('shipping_rules.any_of_hint')}</p>
      )}
    </div>
  );
}
